import { useState } from "react";
import {
  OTHER_OPTION_LABEL,
  isOtherOption,
  concreteOptions,
} from "../../../helpers/multipleOptions/otherOption";

/**
 * De aankruisbare keuzemogelijkheden van een 'kies er meerdere'-vraag op het
 * publieke intakeformulier.
 *
 * De opties komen als prop binnen, niet uit Firestore: deze pagina heeft geen
 * login en haalt haar hele definitie via de Cloud Function op.
 * AI-CONTEXT: See docs/decisions.md#adr-022
 *
 * De selectie is één kommagescheiden tekst, want zo landt hij ook in de
 * gegevens van het huishouden. Een 'Anders, namelijk...'-antwoord is daarin
 * gewoon één van die waarden: de getypte tekst zelf, nooit de markering.
 * Anders staat er straks letterlijk 'other' in de gegevens.
 *
 * @param {object} props
 * @param {Array<{ID: string, Option: string}>} [props.options] - Antwoordopties, al op volgorde
 * @param {string} props.selected - Huidige selectie, als kommagescheiden tekst
 * @param {(value: string) => void} props.onChange - Krijgt de nieuwe selectie
 * @param {string} [props.label] - De vraagtekst; wordt de naam van de GROEP vakjes. Elk vakje
 *   heeft zijn eigen zichtbare tekst, maar zonder groepsnaam hoor je niet bij welke vraag ze horen.
 */
const IntakeFormCheckboxOptions = ({ options, selected, onChange, label }) => {
  const activeOptions = options || [];
  const vasteOpties = concreteOptions(activeOptions);
  const andersOptie = activeOptions.find(isOtherOption);

  const selectedValues = selected ? selected.split(",").map((v) => v.trim()).filter(Boolean) : [];

  const vasteWaarden = vasteOpties.map((o) => o.Option);

  // Een vrije tekst is per definitie géén vaste optie, dus zo is hij terug te
  // vinden zonder hem apart te bewaren. Alleen 'aangekruist maar nog niets
  // getypt' valt daarbuiten — daar is de state voor.
  const andersTekst = selectedValues.find((v) => !vasteWaarden.includes(v)) || "";

  const [andersAangekruist, setAndersAangekruist] = useState(false);
  const toonAndersVeld = andersAangekruist || andersTekst !== "";

  const handleChange = (optionValue, checked) => {
    const next = checked
      ? [...selectedValues, optionValue]
      : selectedValues.filter((v) => v !== optionValue);
    onChange(next.join(", "));
  };

  const handleAndersAankruisen = (checked) => {
    setAndersAangekruist(checked);
    // Uitvinken haalt de getypte tekst weg: hij hoort bij een vinkje dat niet
    // meer staat, en zou anders stil in het antwoord blijven zitten.
    if (!checked && andersTekst) {
      onChange(selectedValues.filter((v) => v !== andersTekst).join(", "));
    }
  };

  const handleAndersTypen = (tekst) => {
    const zonderOud = selectedValues.filter((v) => v !== andersTekst);
    const nieuw = tekst.trim() ? [...zonderOud, tekst] : zonderOud;
    onChange(nieuw.join(", "));
  };

  return (
    <div className="checkbox-options-container" role="group" aria-label={label}>
      {vasteOpties.map((option) => (
        <label key={option.ID} className="checkbox-option-label">
          <input
            type="checkbox"
            value={option.Option}
            checked={selectedValues.includes(option.Option)}
            onChange={(e) => handleChange(option.Option, e.target.checked)}
          />
          {option.Option}
        </label>
      ))}

      {andersOptie && (
        <>
          <label key={andersOptie.ID} className="checkbox-option-label">
            <input
              type="checkbox"
              checked={toonAndersVeld}
              onChange={(e) => handleAndersAankruisen(e.target.checked)}
            />
            {OTHER_OPTION_LABEL}
          </label>
          {toonAndersVeld && (
            <input
              type="text"
              className="intake-other-input"
              // Een eigen naam, niet die van het vinkje ernaast: twee
              // bedieningselementen met dezelfde naam zijn voor een schermlezer
              // niet uit elkaar te houden.
              aria-label={`Jouw antwoord bij ${OTHER_OPTION_LABEL}`}
              value={andersTekst}
              placeholder="Vul hier je antwoord in"
              onChange={(e) => handleAndersTypen(e.target.value)}
            />
          )}
        </>
      )}
    </div>
  );
};

export default IntakeFormCheckboxOptions;
